import { Bell, Mail, MessageCircle, Send } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SaveIndicator } from '@/components/ui/block-editor/autosave';
import { Switch } from '@/components/ui/switch';
import { useSavedFlash } from '@/hooks/useSavedFlash';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../queries';
import type {
  NotificationChannel,
  NotificationPreference,
  NotificationPreferenceUpdate,
} from '../types';

interface ChannelRow {
  key: NotificationChannel;
  label: string;
  description: string;
  icon: typeof Bell;
}

const CHANNELS: ChannelRow[] = [
  {
    key: 'in_app',
    label: 'In-app',
    description:
      'Live updates and pop-ups while the app is open. The bell keeps every notification either way.',
    icon: Bell,
  },
  {
    key: 'email',
    label: 'Email',
    description: 'Send a message to your account email.',
    icon: Mail,
  },
  {
    key: 'slack',
    label: 'Slack',
    description:
      'DM you in Slack. Requires your Slack user id on your profile and the Slack integration configured.',
    icon: MessageCircle,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    description:
      'DM you via Telegram. Requires your Telegram chat id on your profile and the bot configured.',
    icon: Send,
  },
];

const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.key, c.label])) as Record<
  NotificationChannel,
  string
>;

/** `/settings/notifications` — per-user channel and per-type preferences in the active org. */
export function NotificationPreferencesPage() {
  const prefs = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const { saved, flash } = useSavedFlash();

  function save(input: NotificationPreferenceUpdate) {
    update.mutate(input, { onSuccess: flash });
  }

  const state = update.isPending ? 'saving' : update.isError ? 'error' : saved ? 'saved' : 'idle';

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notification preferences</h1>
          <p className="text-muted-foreground text-sm">
            Choose which channels deliver alerts about activity in this organization — and, per
            kind of notification, which of them you want.
          </p>
        </div>
        <SaveIndicator state={state} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Delivery channels</CardTitle>
          <CardDescription>
            Changes save automatically and apply to the current organization. A channel that is
            off delivers nothing, whatever the settings below say.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {prefs.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : prefs.isError || !prefs.data ? (
            <p className="text-destructive text-sm">Failed to load preferences.</p>
          ) : (
            CHANNELS.map(({ key, label, description, icon: Icon }) => (
              <div
                key={key}
                className="flex items-start justify-between gap-3 border-b pb-4 last:border-b-0 last:pb-0"
              >
                <div className="flex items-start gap-3">
                  <Icon className="text-muted-foreground mt-0.5 size-4" />
                  <div>
                    <p className="font-medium">{label}</p>
                    <p className="text-muted-foreground text-sm">{description}</p>
                  </div>
                </div>
                <Switch
                  checked={prefs.data[key]}
                  onCheckedChange={(next) => save({ [key]: next })}
                  disabled={update.isPending}
                  aria-label={`Toggle ${label} notifications`}
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {!prefs.isError && prefs.data?.catalog ? (
        <EventGrid
          prefs={prefs.data}
          catalog={prefs.data.catalog}
          disabled={update.isPending}
          onChange={save}
        />
      ) : null}
    </div>
  );
}

/** One row per notification type (the server's catalog), one switch per channel. */
function EventGrid({
  prefs,
  catalog,
  disabled,
  onChange,
}: {
  prefs: NotificationPreference;
  catalog: NonNullable<NotificationPreference['catalog']>;
  disabled: boolean;
  onChange: (input: NotificationPreferenceUpdate) => void;
}) {
  const { types, channels } = catalog;

  return (
    <Card>
      <CardHeader>
        <CardTitle>By notification type</CardTitle>
        <CardDescription>
          Switch a kind of notification off for one channel and keep it on the others. Greyed out
          means that channel is off above.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th scope="col" className="pb-2 font-medium">
                Notification
              </th>
              {channels.map((channel) => (
                <th key={channel} scope="col" className="pb-2 text-center font-medium">
                  {CHANNEL_LABEL[channel] ?? channel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {types.map(({ key, label }) => (
              <tr key={key}>
                <th scope="row" className="py-2.5 pr-3 text-left font-normal">
                  {label}
                </th>
                {channels.map((channel) => (
                  <td key={channel} className="py-2.5 text-center">
                    <Switch
                      checked={prefs.events?.[key]?.[channel] ?? true}
                      onCheckedChange={(next) => onChange({ events: { [key]: { [channel]: next } } })}
                      disabled={disabled || prefs[channel] === false}
                      aria-label={`${label}: ${CHANNEL_LABEL[channel] ?? channel}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
